<template>
  <div class="inventory-history-page">
    <b-card class="mb-2">
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="5"
        >
          <b-form-group
            v-if="isAdmin"
            label="Dépôt"
          >
            <v-select
              v-model="selectedRepository"
              :options="repositories"
              label="name"
              :clearable="false"
              placeholder="Choisir un dépôt"
              @input="fetchSessions"
            />
          </b-form-group>
          <b-form-group
            v-else
            label="Dépôt"
          >
            <b-form-input
              :value="selectedRepository ? selectedRepository.name : ''"
              disabled
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-card title="Historique des inventaires">
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group label="Créé le">
            <b-form-input
              v-model="openedDateFilter"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group label="Créé par">
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="createdByFilter"
                placeholder="Rechercher par utilisateur"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-end"
        >
          <b-button
            variant="outline-secondary"
            class="mb-1"
            :disabled="!openedDateFilter && !createdByFilter"
            @click="resetFilters"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-50"
            />
            Réinitialiser
          </b-button>
        </b-col>
      </b-row>
      <b-table
        small
        responsive
        :items="filteredSessions"
        :fields="sessionFields"
        :busy="loading"
        empty-text="Aucun inventaire"
        show-empty
      >
        <template #cell(status)="data">
          <b-badge :variant="data.item.status === 'closed' ? 'success' : 'info'">
            {{ data.item.status === 'closed' ? 'Clôturé' : 'Ouvert' }}
          </b-badge>
        </template>
        <template #cell(progress)="data">
          {{ data.item.scanned_total }} / {{ data.item.expected_total }}
        </template>
        <template #cell(opened_by_username)="data">
          {{ data.item.opened_by_username || '-' }}
        </template>
        <template #cell(closed_by_username)="data">
          {{ data.item.closed_by_username || '-' }}
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            :to="{ name: 'inventory-dashboard', query: { session: data.item.id } }"
          >
            <feather-icon
              icon="EyeIcon"
              size="14"
            />
          </b-button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BRow,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BRow,
    BTable,
    vSelect,
  },
  data() {
    return {
      user: {},
      repositories: [],
      selectedRepository: null,
      sessions: [],
      openedDateFilter: '',
      createdByFilter: '',
      loading: false,
      sessionFields: [
        { key: 'id', label: 'N°' },
        { key: 'repository.name', label: 'Dépôt' },
        { key: 'status', label: 'Statut' },
        { key: 'progress', label: 'Scannés' },
        { key: 'missing_total', label: 'Manquants' },
        { key: 'opened_by_username', label: 'Créé par' },
        { key: 'closed_by_username', label: 'Clôturé par' },
        { key: 'opened_at', label: 'Ouvert le' },
        { key: 'closed_at', label: 'Clôturé le' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    isAdmin() {
      return ['admin', 'superadmin'].includes(this.user.role)
    },
    filteredSessions() {
      const createdBySearch = String(this.createdByFilter || '').trim().toLowerCase()

      return this.sessions.filter(session => {
        const matchesDate = !this.openedDateFilter
          || String(session.opened_at || '').slice(0, 10) === this.openedDateFilter
        const matchesCreator = !createdBySearch
          || String(session.opened_by_username || '').toLowerCase().includes(createdBySearch)

        return matchesDate && matchesCreator
      })
    },
  },
  async created() {
    this.user = storeAuth.state.user || {}
    await this.initializeRepository()
  },
  methods: {
    async initializeRepository() {
      if (this.isAdmin) {
        await this.fetchRepositories()
        if (this.repositories.length > 0) {
          const [firstRepository] = this.repositories
          this.selectedRepository = firstRepository
          await this.fetchSessions()
        }
      } else {
        const repository = this.normalizeUserRepository()
        if (repository) {
          this.selectedRepository = repository
          await this.fetchSessions()
        }
      }
    },
    normalizeUserRepository() {
      if (!this.user.repository) return null
      if (typeof this.user.repository === 'object') return this.user.repository
      return {
        id: this.user.repository,
        name: this.user.repository_name || `Dépôt ${this.user.repository}`,
      }
    },
    async fetchRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    async fetchSessions() {
      if (!this.selectedRepository) return
      this.loading = true
      try {
        const { data } = await axios.get('/api/inventory/sessions/', {
          params: { repository: this.selectedRepository.id },
        })
        this.sessions = data.results || data
      } catch (error) {
        this.showToast('danger', this.errorMessage(error))
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.openedDateFilter = ''
      this.createdByFilter = ''
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Inventaire',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
    errorMessage(error) {
      if (error.response && error.response.data) {
        return error.response.data.error || error.response.data.detail || error.toString()
      }
      return error.toString()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
